import { useCallback, useEffect, useState } from 'react';
import type { Meta } from '@dash/shared';
import { apiFetch, ApiError } from '../lib/api.ts';
import { fmtInt, fmtCurrency } from '../lib/format.ts';

interface AdminMetasProps {
  token?: string;
}

type Area = 'vendas' | 'os' | 'churn';
type Periodo = 'dia' | 'mes';

interface FormState {
  area: Area;
  periodo: Periodo;
  valor: string;
  vigente_desde: string;
  vigente_ate: string;
}

const TODAY_ISO = () => new Date().toISOString().slice(0, 10);

const EMPTY_FORM: FormState = {
  area: 'vendas',
  periodo: 'mes',
  valor: '',
  vigente_desde: TODAY_ISO(),
  vigente_ate: '',
};

export function AdminMetasView({ token }: AdminMetasProps) {
  const [metas, setMetas] = useState<Meta[]>([]);
  const [vigentes, setVigentes] = useState<Meta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [all, vig] = await Promise.all([
        apiFetch<{ data: Meta[] }>('/metas', { token }),
        apiFetch<{ data: Meta[] }>('/metas/vigentes', { token }),
      ]);
      setMetas(all.data);
      setVigentes(vig.data);
    } catch (e) {
      const msg = e instanceof ApiError ? `${e.code}: ${e.message}` : (e as Error).message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    reload();
  }, [reload]);

  async function handleCreate(ev: React.FormEvent) {
    ev.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await apiFetch('/metas', {
        method: 'POST',
        token,
        body: {
          area: form.area,
          periodo: form.periodo,
          valor: Number(form.valor),
          vigente_desde: form.vigente_desde,
          vigente_ate: form.vigente_ate || null,
        },
      });
      setForm(EMPTY_FORM);
      await reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.code}: ${e.message}` : (e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Soft delete dessa meta?')) return;
    try {
      await apiFetch(`/metas/${id}`, { method: 'DELETE', token });
      await reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.code}: ${e.message}` : (e as Error).message);
    }
  }

  async function handleEncerrar(id: string) {
    const date = prompt('Encerrar vigencia em (YYYY-MM-DD):', TODAY_ISO());
    if (!date) return;
    try {
      await apiFetch(`/metas/${id}`, {
        method: 'PATCH',
        token,
        body: { vigente_ate: date },
      });
      await reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.code}: ${e.message}` : (e as Error).message);
    }
  }

  return (
    <div className="grid gap-6 p-6 lg:grid-cols-12">
      {/* Form criar */}
      <div className="lg:col-span-5">
        <div className="rounded-2xl border border-[--color-border] bg-[--color-card] p-5">
          <h3 className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[--color-fg-muted]">
            Criar nova meta
          </h3>
          <p className="mt-1 text-xs text-[--color-fg-dim]">
            Versionamento automatico — uma meta nova em (area, periodo) substitui a vigente sem
            apagar o historico.
          </p>

          <form onSubmit={handleCreate} className="mt-4 grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Area">
                <select
                  className="w-full rounded-md border border-[--color-border] bg-[--color-bg] px-3 py-2 text-sm"
                  value={form.area}
                  onChange={(e) => setForm({ ...form, area: e.target.value as Area })}
                >
                  <option value="vendas">vendas</option>
                  <option value="os">OS</option>
                  <option value="churn">churn</option>
                </select>
              </Field>
              <Field label="Periodo">
                <select
                  className="w-full rounded-md border border-[--color-border] bg-[--color-bg] px-3 py-2 text-sm"
                  value={form.periodo}
                  onChange={(e) => setForm({ ...form, periodo: e.target.value as Periodo })}
                >
                  <option value="dia">dia</option>
                  <option value="mes">mes</option>
                </select>
              </Field>
            </div>

            <Field label="Valor">
              <input
                required
                type="number"
                step="0.01"
                min="0"
                className="w-full rounded-md border border-[--color-border] bg-[--color-bg] px-3 py-2 text-sm tabular-nums"
                value={form.valor}
                onChange={(e) => setForm({ ...form, valor: e.target.value })}
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Vigente desde">
                <input
                  required
                  type="date"
                  className="w-full rounded-md border border-[--color-border] bg-[--color-bg] px-3 py-2 text-sm"
                  value={form.vigente_desde}
                  onChange={(e) => setForm({ ...form, vigente_desde: e.target.value })}
                />
              </Field>
              <Field label="Vigente ate (opcional)">
                <input
                  type="date"
                  className="w-full rounded-md border border-[--color-border] bg-[--color-bg] px-3 py-2 text-sm"
                  value={form.vigente_ate}
                  onChange={(e) => setForm({ ...form, vigente_ate: e.target.value })}
                />
              </Field>
            </div>

            <button
              type="submit"
              disabled={saving}
              className="mt-2 rounded-md bg-[--color-info] px-4 py-2 text-sm font-semibold text-white transition-opacity disabled:opacity-50"
            >
              {saving ? 'salvando...' : 'criar meta'}
            </button>
          </form>
        </div>

        {/* Vigentes */}
        <div className="mt-4 rounded-2xl border border-[--color-border] bg-[--color-card] p-5">
          <h3 className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[--color-fg-muted]">
            Vigentes ({vigentes.length})
          </h3>
          <div className="mt-3 grid gap-2">
            {vigentes.length === 0 && (
              <p className="text-xs text-[--color-fg-dim]">nenhuma meta vigente — crie ao lado.</p>
            )}
            {vigentes.map((m) => (
              <div
                key={m.id}
                className="flex items-baseline justify-between rounded-md border border-[--color-border] px-3 py-2"
              >
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-[--color-fg-muted]">
                    {m.area} · {m.periodo}
                  </span>
                  <div className="text-2xl font-bold tabular-nums text-[--color-fg]">
                    {m.area === 'churn' ? `${m.valor}%` : fmtInt(m.valor)}
                  </div>
                </div>
                <span className="text-[10px] text-[--color-fg-dim]">
                  desde {m.vigente_desde}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabela completa */}
      <div className="lg:col-span-7">
        <div className="rounded-2xl border border-[--color-border] bg-[--color-card] p-5">
          <div className="flex items-baseline justify-between">
            <h3 className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[--color-fg-muted]">
              Historico ({metas.length})
            </h3>
            <button
              onClick={reload}
              className="text-[10px] uppercase tracking-wider text-[--color-fg-muted] hover:text-[--color-fg]"
            >
              recarregar
            </button>
          </div>

          {error && (
            <div className="mt-3 rounded-md border border-[--color-crit]/40 bg-[--color-crit-soft] px-3 py-2 text-sm text-[--color-crit]">
              {error}
            </div>
          )}

          {loading ? (
            <p className="mt-4 text-sm text-[--color-fg-muted]">carregando...</p>
          ) : (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-wider text-[--color-fg-muted]">
                  <tr className="border-b border-[--color-border]">
                    <th className="py-2 text-left">area</th>
                    <th className="py-2 text-left">periodo</th>
                    <th className="py-2 text-right">valor</th>
                    <th className="py-2 text-left">desde</th>
                    <th className="py-2 text-left">ate</th>
                    <th className="py-2 text-right">acoes</th>
                  </tr>
                </thead>
                <tbody className="tabular-nums">
                  {metas.map((m) => {
                    const isVigente = vigentes.some((v) => v.id === m.id);
                    const valorFmt =
                      m.area === 'churn' ? `${m.valor}%` : m.area === 'vendas' && m.periodo === 'mes' ? fmtCurrency(m.valor) : fmtInt(m.valor);
                    return (
                      <tr key={m.id} className="border-b border-[--color-border]/40">
                        <td className="py-2">
                          {m.area}
                          {isVigente && (
                            <span className="ml-2 rounded bg-[--color-ok]/20 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-[--color-ok]">
                              vigente
                            </span>
                          )}
                        </td>
                        <td className="py-2">{m.periodo}</td>
                        <td className="py-2 text-right">{valorFmt}</td>
                        <td className="py-2">{m.vigente_desde}</td>
                        <td className="py-2 text-[--color-fg-dim]">{m.vigente_ate ?? '—'}</td>
                        <td className="py-2 text-right">
                          <button
                            onClick={() => handleEncerrar(m.id)}
                            className="mr-2 text-xs text-[--color-warn] hover:underline"
                          >
                            encerrar
                          </button>
                          <button
                            onClick={() => handleDelete(m.id)}
                            className="text-xs text-[--color-crit] hover:underline"
                          >
                            apagar
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {metas.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-6 text-center text-[--color-fg-muted]">
                        nenhuma meta cadastrada ainda.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="grid gap-1">
      <span className="text-[10px] uppercase tracking-wider text-[--color-fg-muted]">{label}</span>
      {children}
    </label>
  );
}
